/**
 * Animations d'entrée du bloc « Ma maîtrise » : un nombre qui compte jusqu'à sa
 * valeur, un compteur qui monte par paliers (effets d'aura un par un). Coupées
 * si l'appareil demande de réduire les animations (valeur finale directe).
 */

import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@jeraisonne/ui';

/** Départ rapide, arrivée douce. */
export function easeOutCubic(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) ** 3;
}

/** Valeur à l'instant `elapsed` (ms) d'une animation `from → to` de `duration` ms. */
export function countUpValue(from: number, to: number, elapsed: number, duration: number): number {
  if (duration <= 0 || elapsed >= duration) return to;
  if (elapsed <= 0) return from;
  return from + (to - from) * easeOutCubic(elapsed / duration);
}

/** Nombre qui compte de sa valeur précédente (0 au montage) jusqu'à `target`. */
export function useCountUp(target: number, { duration = 1400, delay = 0 } = {}): number {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(reduced ? target : 0);
  const current = useRef(value);

  useEffect(() => {
    if (reduced) {
      current.current = target;
      setValue(target);
      return;
    }
    const from = current.current;
    let raf = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now + delay;
      const v = countUpValue(from, target, now - start, duration);
      current.current = v;
      setValue(v);
      if (v !== target) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, delay, reduced]);

  return value;
}

/** Compteur 0 → `max`, un pas toutes les `stepMs` (après `delay`), une seule fois. */
export function useStepUp(max: number, { stepMs = 160, delay = 0 } = {}): number {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(reduced ? max : 0);

  useEffect(() => {
    if (reduced) {
      setStep(max);
      return;
    }
    let timer = 0;
    const next = (n: number) => {
      setStep(n);
      if (n < max) timer = window.setTimeout(() => next(n + 1), stepMs);
    };
    timer = window.setTimeout(() => next(1), delay);
    return () => window.clearTimeout(timer);
  }, [max, stepMs, delay, reduced]);

  return step;
}

/** `false` au premier rendu, `true` juste après : déclenche une transition CSS d'entrée. */
export function useEntered(): boolean {
  const reduced = usePrefersReducedMotion();
  const [entered, setEntered] = useState(reduced);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return entered;
}
